import { useState } from 'react';
import { AxiosError } from 'axios';
import { useQueries } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import Header from '../../../../customs/Header';
import apiCall from '../../../../utils/apiCall';
import LgaTable from './Table';
import { FilterDropDown } from '../../../../ui-components';
import SettingsModal from '../Modal/settingsCustomModal';
import CreateLGA from './CreateLga';
import styles from '../../Applicants/css/display-applicant.module.css';
import {AiOutlineSearch as SearchIcon} from 'react-icons/ai';
import {IoReload as ReloadIcon} from 'react-icons/io5';

interface LGAByState {
  LGAId: number;
  LGAName: string;
  StateId: number;
  State?: any;
}

interface Lga {
  LGAId: number;
  LGAName: string;
  StateId: number;
  State: LgaState;
}

interface LgaState {
  StateId: number;
  StateName: string;
  CountryId: number;
  Country?: any;
  Id: number;
  CreatedBy?: any;
  ModifiedBy?: any;
  UserIpAddress?: any;
  CreateDate: string;
  LastModified: string;
  ActiveStatus: boolean;
  IsApprovalRequired?: any;
  RouteId?: any;
  ApprovalTypeId?: any;
  IsDeleted: boolean;
}

interface State {
  StateId: number;
  StateName: string;
  CountryId?: number;
  Country: CountryByState;
}

interface CountryByState {
  CountryId: number;
  CountryName: string;
  Id: number;
  CreatedBy?: any;
  ModifiedBy?: any;
  UserIpAddress?: any;
  CreateDate: string;
  LastModified: string;
  ActiveStatus: boolean;
  IsApprovalRequired?: any;
  RouteId?: any;
  ApprovalTypeId?: any;
  IsDeleted: boolean;
}

export default function Main() {
  const [createLGA, setCreateLGA] = useState(false);
  const [state, setState] = useState({val: '0', name: ''});
  const [searchData, setSearchData] = useState('')
  
  const handleOpenCreateLGA = () => setCreateLGA(true);
  const handleCloseCreateLGA = () => setCreateLGA(false);

  const stateId = state ? state?.val : '';

  const getAllLga = async (): Promise<Lga[]> => {
    return (await apiCall().get('/LGA/GetAllLGAs')).data
  };

  const getStates = async (): Promise<State[]> => {
    return (await apiCall().get('/State/GetAllStates')).data;
  };

  const getLGAByState = async (): Promise<LGAByState[]> => {
    return (await apiCall().get(`/LGA/GetLGAByState/${stateId}`)).data;
  };

  const [getAllLgaQuery, getAllStateQuery, getLGAByStateQuery] = useQueries(
    {
      queries: [
        {queryKey: ['get-all-lga'], queryFn: getAllLga},
        {queryKey: ['get-states'], queryFn: getStates, enabled: true},
        {queryKey: ['get-lga-by-state', stateId, searchData], queryFn: getLGAByState},
      ]
    }
  );

  const filteredLGA = getAllLgaQuery?.data?.filter((data1: any) => {
    return stateId === '0' ? data1 : getLGAByStateQuery?.data?.some((data2: any) => {
      return data1.LGAId === data2.LGAId;
    })
  });

  const getStateMessage = getAllStateQuery.error as AxiosError;
  const getStateErrorMessage = getStateMessage?.message;

  const getLGAMessage = getAllLgaQuery.error as AxiosError;
  const getLGAErrorMessage = getLGAMessage?.message;

  const getLGAByStateMessage = getLGAByStateQuery?.error as AxiosError;
  const getLGAByStateErrorMessage = getLGAByStateMessage?.message;

  const mappedState: any[] | undefined = getAllStateQuery?.data && getAllStateQuery?.data?.map((item) => {
    let state = {
      val: item?.StateId,
      name: item?.StateName,
    }
    return state;
  });

  const filteredData = filteredLGA?.filter(x => (
    Object.values(x).join(" ").toLowerCase().includes(searchData.toLowerCase())
  ));

  return (
    <section>
      <Header props={
        { 
          firstName: 'All', 
          secondName: 'Local Government Area', 
          buttonText: 'Create LGA', 
        }}
        handleOpen={handleOpenCreateLGA}
      />

      <InnerContainer >
        <label className={styles.searchForm} >
          <span className={styles.sI}><SearchIcon/></span>
          <input value = {searchData} onChange={(e) => setSearchData(e.target.value)} type="text" placeholder='Search' />
          <button className={styles.rBtn} type="submit">
            <ReloadIcon/>
          </button>
        </label>
        <FilterDropDown onChange={setState} options={mappedState} text="State" bg="#11643C" color="#fff" />
      </InnerContainer>
      <StyledContainer>
        {getLGAByStateQuery?.isFetching ? (
          <CircularProgress
              sx={{
                  fontSize: '1rem',
                  marginInline: 'auto',
                  display: 'block',
                  marginBlock: '2rem'
              }} 
          />
          ) : (stateId === '0' ? getAllLgaQuery?.isError : getLGAByStateQuery?.isError) ? (
          <StyledError>{stateId === '0' ? getLGAErrorMessage : getLGAByStateErrorMessage}</StyledError>
          ) :  filteredData && filteredData?.length > 0 ?  (
          <LgaTable 
            getLgaQuery={searchData.length > 0 ? filteredData :filteredLGA}
            getStateData={getAllStateQuery?.data}
            stateIsError={getAllStateQuery?.isError}
            stateIsLoading={getAllStateQuery?.isLoading}
            getStateErrorMessage={getStateErrorMessage}
          />
        ) : <NoResult>No Results</NoResult>}
      </StyledContainer>

      <SettingsModal 
        open={createLGA}
        cancel={handleCloseCreateLGA}
        maxWidth='480px'
        header={'Create LGA'}
        showrightIcon={false}
        content={
          <CreateLGA 
            handleClose={handleCloseCreateLGA}
            data={getAllStateQuery?.data}
            isLoading={getAllStateQuery.isLoading}
            isError={getAllStateQuery?.isError}
            getStateErrorMessage={getStateErrorMessage}
          />
        }
      />
      
    </section>
  )
}

const InnerContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  margin-inline-start: 48px;
`

const StyledContainer = styled.div`
    margin-block: 3.8rem;
    margin-inline: 3.5rem;
`

const StyledError = styled('p')`
  color: red;
  font-size: 1.4rem;
  display: flex;
`
const NoResult = styled('div')`
  margin-block-start: 0px;
`
